// Live mic capture → Opus (WebCodecs) → binary WebSocket, with a single-speaker control
// protocol, auto-reconnect, and a 1-byte/s liveness heartbeat.
//
// WebCodecs (`AudioEncoder`/`AudioData`) is not in this TS version's lib.dom, so those
// globals are reached via `window as any` and typed loosely on purpose.

type StatusState = "connecting" | "live" | "reconnecting" | "revoked" | "contested";
export type StatusFn = (state: StatusState, info?: string | null) => void;

export interface AudioStreamOpts {
    stream?: MediaStream; // caller-supplied source; defaults to mic capture
    bitrate?: number; // Opus bitrate (default 32000)
    name?: string; // display name shown to other clients
    onStatus?: StatusFn;
}

const W = window as any;

// --- logging: everything tagged [LiveAudio]. Lifecycle → log/warn/error; the 1 Hz
// heartbeat/ack → debug (filterable, won't flood the console). ---
const log = (...a: unknown[]) => console.log("[LiveAudio]", ...a);
const logWarn = (...a: unknown[]) => console.warn("[LiveAudio]", ...a);
const logErr = (...a: unknown[]) => console.error("[LiveAudio]", ...a);
const logDbg = (...a: unknown[]) => console.debug("[LiveAudio]", ...a);

let ctx: AudioContext | null = null;
let node: AudioWorkletNode | null = null;
let encoder: any = null;
let ws: WebSocket | null = null;
let running = false;
let wsReady = false;
let hasControl = false;
let seq = 0;
let sampleClock = 0;
let wsUrl_: string | null = null;
let hello_: Record<string, unknown> | null = null;
let clientId: string | null = null; // stable per module load; used for identity-based resume
let pendingTake = false; // user wants the speaker; awaiting resolution (grant or takeover decision)
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let backoff = 250; // ms; grows to MAX_BACKOFF
let onStatus_: StatusFn = () => {};
const MIN_BACKOFF = 250;
const MAX_BACKOFF = 5000;

// --- liveness heartbeat: 1 byte out / 1 s, sever if no ack for >= 5 s ---
let hbTimer: ReturnType<typeof setInterval> | null = null;
let lastAckAt = 0;
const HB = new Uint8Array([0x00]); // 1-byte ping; length is the signal, value ignored
const HB_INTERVAL_MS = 1000;
const HB_DEADLINE_MS = 5000;

function startHeartbeat() {
    lastAckAt = Date.now(); // fresh socket counts as alive
    if (hbTimer) clearInterval(hbTimer);
    log(`heartbeat started (${HB_INTERVAL_MS}ms interval, ${HB_DEADLINE_MS}ms deadline)`);
    hbTimer = setInterval(() => {
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        const sinceAck = Date.now() - lastAckAt;
        if (sinceAck >= HB_DEADLINE_MS) {
            // backend/link is dead
            logWarn(`no heartbeat ack for ${sinceAck}ms (>= ${HB_DEADLINE_MS}) — closing socket`);
            try {
                ws.close();
            } catch {
                /* ignore */
            }
            return;
        }
        try {
            ws.send(HB); // 1-byte heartbeat
            logDbg("heartbeat → sent");
        } catch (e) {
            logWarn("heartbeat send failed", e);
        }
    }, HB_INTERVAL_MS);
}

function stopHeartbeat() {
    if (hbTimer) {
        clearInterval(hbTimer);
        log("heartbeat stopped");
    }
    hbTimer = null;
}

// --- reconnecting WebSocket: rebuilds the socket only; capture+encoder stay alive ---
function connectWs() {
    onStatus_("connecting");
    log(`connecting WebSocket → ${wsUrl_}`);
    ws = new WebSocket(wsUrl_!);
    ws.binaryType = "arraybuffer";
    ws.onopen = () => {
        backoff = MIN_BACKOFF;
        log("WebSocket open — sending hello", { clientId, name: hello_?.name });
        ws!.send(JSON.stringify(hello_));
        startHeartbeat();
    };
    ws.onmessage = (ev: MessageEvent) => {
        if (typeof ev.data !== "string") {
            // binary from server = 1-byte heartbeat ack
            if (ev.data.byteLength === 1) {
                lastAckAt = Date.now();
                logDbg("heartbeat ack ← received");
            } else {
                logWarn("unexpected binary message from server", ev.data.byteLength, "bytes");
            }
            return;
        }
        let m: any;
        try {
            m = JSON.parse(ev.data);
        } catch {
            logWarn("could not parse control frame", ev.data);
            return;
        }
        log("control frame ←", m);
        switch (m.type) {
            case "ready":
                // Server did NOT resume us.
                wsReady = true;
                if (pendingTake) {
                    // The user wants the speaker. If it's free, take it uncontested (no prompt);
                    // if someone holds it, ask the UI to confirm a takeover before we seize.
                    // Keep pendingTake set until resolved (grant/stop) so a reconnect re-evaluates.
                    if (!m.controlled) {
                        log("speaker is free — seizing (uncontested)");
                        ws!.send(JSON.stringify({ type: "seize" }));
                    } else {
                        log("speaker is held by someone else — prompting for takeover");
                        onStatus_("contested"); // → "someone's speaking, take over?" prompt
                    }
                } else if (hasControl) {
                    // reconnected but lost the speaker meanwhile
                    logWarn("reconnected but no longer hold the speaker — treating as revoked");
                    hasControl = false;
                    onStatus_("revoked", null); // someone took it / grace expired
                    stopAudioStream();
                } else {
                    log("ready received; not taking the speaker (no pending request)");
                }
                break;
            case "granted": // fresh seize grant OR identity resume
                log("speaker GRANTED — going live");
                hasControl = true;
                pendingTake = false;
                onStatus_("live"); // → animated waveform icon
                break;
            case "revoked":
                logWarn(`speaker REVOKED by ${m.by ?? "someone"} — stopping`);
                hasControl = false;
                onStatus_("revoked", m.by); // → "X took the speaker" bubble, then stop
                stopAudioStream(); // release mic/encoder; we no longer hold it
                break;
            default:
                logWarn("unknown control frame type", m.type);
        }
    };
    ws.onclose = (ev: CloseEvent) => {
        log(`WebSocket closed (code=${ev.code}, reason="${ev.reason}", clean=${ev.wasClean})`);
        stopHeartbeat();
        wsReady = false;
        hasControl = false;
        if (running) {
            onStatus_("reconnecting");
            scheduleReconnect();
        }
    };
    ws.onerror = () => {
        logWarn("WebSocket error — closing to trigger reconnect");
        try {
            ws?.close();
        } catch {
            /* ignore */
        }
    }; // → onclose → reconnect
}

function scheduleReconnect() {
    if (!running || reconnectTimer) return; // not while stopped / already queued
    const delay = Math.min(backoff, MAX_BACKOFF) * (0.5 + Math.random()); // jittered
    log(`scheduling reconnect in ${Math.round(delay)}ms (backoff ${backoff}ms)`);
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        backoff = Math.min(backoff * 2, MAX_BACKOFF); // exponential backoff
        connectWs();
    }, delay);
}

export async function startAudioStream(wsUrl: string, opts: AudioStreamOpts = {}): Promise<void> {
    if (running) {
        logWarn("startAudioStream ignored — already running");
        return;
    }
    log("startAudioStream()", { wsUrl, name: opts.name, bitrate: opts.bitrate, hasStream: !!opts.stream });

    let source: MediaStream;
    if (opts.stream) {
        log("using caller-supplied MediaStream");
        source = opts.stream;
    } else {
        log("requesting microphone (getUserMedia)…");
        try {
            source = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
                video: false,
            });
        } catch (e) {
            logErr("getUserMedia failed (mic permission denied or no device?)", e);
            throw e;
        }
        log(
            "microphone granted:",
            source.getAudioTracks().map((t) => t.label || "(unnamed track)"),
        );
    }

    log("creating AudioContext @48kHz");
    ctx = new AudioContext({ sampleRate: 48000, latencyHint: "interactive" });
    log(`AudioContext state=${ctx.state}, actual sampleRate=${ctx.sampleRate}`);
    log("loading AudioWorklet module /audio/pcm-capture-worklet.js…");
    try {
        await ctx.audioWorklet.addModule("/audio/pcm-capture-worklet.js");
    } catch (e) {
        logErr("failed to load pcm-capture-worklet.js (is it served at /audio/?)", e);
        throw e;
    }
    log("worklet loaded — wiring MediaStreamSource → pcm-capture node");

    const src = ctx.createMediaStreamSource(source);
    node = new AudioWorkletNode(ctx, "pcm-capture", { numberOfInputs: 1, numberOfOutputs: 0 });
    src.connect(node);

    // --- WebCodecs Opus encoder (created before the socket; runs continuously) ---
    if (!("AudioEncoder" in window)) {
        logErr("WebCodecs AudioEncoder unavailable in this browser");
        throw new Error("WebCodecs AudioEncoder unavailable — use MediaRecorder fallback");
    }

    let firstFrameLogged = false;
    encoder = new W.AudioEncoder({
        output: (chunk: any) => {
            // Send only while we hold the speaker and the link is up — live stream, no backlog.
            if (!hasControl || !wsReady || !ws || ws.readyState !== WebSocket.OPEN) return;
            if (ws.bufferedAmount > 8192) {
                logDbg(`backpressure: skipping frame (bufferedAmount=${ws.bufferedAmount})`);
                return; // backpressure: skip rather than lag
            }
            const payload = new Uint8Array(chunk.byteLength);
            chunk.copyTo(payload);
            const msg = new Uint8Array(8 + payload.byteLength);
            const dv = new DataView(msg.buffer);
            dv.setUint32(0, seq >>> 0, true);
            dv.setUint32(4, sampleClock >>> 0, true);
            msg.set(payload, 8);
            ws.send(msg);
            seq++;
            if (!firstFrameLogged) {
                log("streaming audio — first Opus frame sent");
                firstFrameLogged = true;
            }
        },
        error: (e: unknown) => logErr("Opus encoder error", e),
    });
    log("configuring Opus encoder", { sampleRate: 48000, channels: 1, bitrate: opts.bitrate ?? 32000 });
    encoder.configure({
        codec: "opus",
        sampleRate: 48000,
        numberOfChannels: 1,
        bitrate: opts.bitrate ?? 32000,
        opus: { frameDuration: 20000, application: "lowdelay" }, // 20 ms frames, low-delay mode
    });

    // --- feed PCM from the worklet into the encoder ---
    node.port.onmessage = (ev: MessageEvent) => {
        if (!running) return;
        const pcm = ev.data as Float32Array; // mono @48k, 128 samples
        const data = new W.AudioData({
            format: "f32",
            sampleRate: 48000,
            numberOfFrames: pcm.length,
            numberOfChannels: 1,
            timestamp: Math.round((sampleClock / 48000) * 1e6), // microseconds, monotonic
            data: pcm,
        });
        sampleClock += pcm.length;
        encoder.encode(data);
        data.close();
    };

    // --- open the (reconnecting) socket last, once the pipeline is ready ---
    running = true; // must be true so reconnect is armed
    wsUrl_ = wsUrl;
    onStatus_ = opts.onStatus ?? (() => {});
    clientId = clientId ?? crypto.randomUUID(); // stable across this session's reconnects
    pendingTake = true; // the user wants the speaker; resolved on `ready` (free → take, held → prompt)
    hello_ = {
        type: "hello",
        clientId,
        codec: "opus",
        sampleRate: 48000,
        channels: 1,
        frameMs: 20,
        name: opts.name ?? "Someone",
    };
    backoff = MIN_BACKOFF;
    connectWs(); // resolves start immediately; retries in bg
}

/**
 * Confirm a contested takeover: the speaker was held by someone else (onStatus "contested")
 * and the user chose to take it anyway. Sends the contested `seize`. No-op if the link isn't
 * up or the user isn't awaiting a decision.
 */
export function seizeControl(): void {
    if (!pendingTake || !wsReady || !ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "seize" }));
}

export async function stopAudioStream(): Promise<void> {
    running = false; // disarms scheduleReconnect()
    stopHeartbeat(); // onclose is nulled below, so stop it here
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    try {
        if (ws && ws.readyState === WebSocket.OPEN && hasControl)
            ws.send(JSON.stringify({ type: "release" })); // yield the speaker
    } catch {
        /* ignore */
    }
    try {
        if (node) node.port.onmessage = null;
    } catch {
        /* ignore */
    }
    try {
        if (encoder && encoder.state !== "closed") await encoder.flush();
    } catch {
        /* ignore */
    }
    try {
        if (encoder) encoder.close();
    } catch {
        /* ignore */
    }
    try {
        if (node) node.disconnect();
    } catch {
        /* ignore */
    }
    try {
        if (ctx) await ctx.close();
    } catch {
        /* ignore */
    }
    try {
        if (ws) {
            ws.onclose = null;
            ws.close();
        } // null first: no reconnect
    } catch {
        /* ignore */
    }
    encoder = node = ctx = ws = null;
    wsReady = hasControl = pendingTake = false;
    seq = 0;
    sampleClock = 0;
}
